export type SpellElement = 'fire' | 'frost' | 'storm' | 'venom' | 'void' | 'arcane';

export type ElementalReactionKind =
  | 'shatter'
  | 'superconduct'
  | 'toxic_ignition'
  | 'singularity_shock';

export const SPELL_ELEMENTS: Record<string, SpellElement> = {
  arcane_bolt: 'arcane',
  fireball: 'fire',
  frost_nova: 'frost',
  chain_lightning: 'storm',
  spirit_orbs: 'arcane',
  venom_pool: 'venom',
  radiant_lance: 'fire',
  wind_scythe: 'storm',
  earth_spike: 'arcane',
  shadow_dagger: 'void',
  gravity_well: 'void',
  sunfire_aura: 'fire',
  crystal_shards: 'frost',
  blood_siphon: 'void',
  rune_trap: 'fire',
  tidal_wave: 'frost',
};

export interface ElementalStatus {
  fireUntil: number;
  frostUntil: number;
  stormUntil: number;
  venomUntil: number;
  voidUntil: number;
  cooldownUntil: number;
}

export interface ElementalReactionOut {
  reaction: ElementalReactionKind | null;
  bonusDamage: number;
  aoeRadius: number;
  vulnDuration: number;
  stunDuration: number;
  spreadVenom: boolean;
  color: string;
}

const NO_REACTION: ElementalReactionOut = {
  reaction: null,
  bonusDamage: 0,
  aoeRadius: 0,
  vulnDuration: 0,
  stunDuration: 0,
  spreadVenom: false,
  color: '',
};

const PRIME_DURATION = 4.0;
const REACTION_COOLDOWN = 0.85;

export function emptyElementalStatus(): ElementalStatus {
  return {
    fireUntil: 0,
    frostUntil: 0,
    stormUntil: 0,
    venomUntil: 0,
    voidUntil: 0,
    cooldownUntil: 0,
  };
}

export function clearElementalStatus(status: ElementalStatus): void {
  status.fireUntil = 0;
  status.frostUntil = 0;
  status.stormUntil = 0;
  status.venomUntil = 0;
  status.voidUntil = 0;
  status.cooldownUntil = 0;
}

export function resolveElementalReaction(
  status: ElementalStatus,
  incoming: SpellElement,
  now: number,
  baseDamage: number,
): ElementalReactionOut {
  if (incoming === 'arcane') return NO_REACTION;

  const hasFire = status.fireUntil > now;
  const hasFrost = status.frostUntil > now;
  const hasStorm = status.stormUntil > now;
  const hasVenom = status.venomUntil > now;
  const hasVoid = status.voidUntil > now;

  if (now >= status.cooldownUntil) {
    // 1. Shatter: Frost + Fire -> 2.2x total burst (1.2x bonus) in 3.0m AoE, consumes Frost & Fire
    if ((incoming === 'fire' && hasFrost) || (incoming === 'frost' && hasFire)) {
      status.frostUntil = 0;
      status.fireUntil = 0;
      status.cooldownUntil = now + REACTION_COOLDOWN;
      return {
        reaction: 'shatter',
        bonusDamage: baseDamage * 1.2,
        aoeRadius: 3.0,
        vulnDuration: 0,
        stunDuration: 0,
        spreadVenom: false,
        color: '#9ee8ff',
      };
    }

    // 2. Superconduct: Frost + Storm -> +45% shock bonus & 4.0s vulnerability (+25% damage taken)
    if ((incoming === 'storm' && hasFrost) || (incoming === 'frost' && hasStorm)) {
      status.frostUntil = 0;
      status.stormUntil = 0;
      status.cooldownUntil = now + REACTION_COOLDOWN;
      return {
        reaction: 'superconduct',
        bonusDamage: baseDamage * 0.45,
        aoeRadius: 2.4,
        vulnDuration: 4.0,
        stunDuration: 0,
        spreadVenom: false,
        color: '#7be3ff',
      };
    }

    // 3. Toxic Ignition: Venom + Fire -> +80% virulent blast in 3.2m AoE & spreads venom
    if ((incoming === 'fire' && hasVenom) || (incoming === 'venom' && hasFire)) {
      status.venomUntil = 0;
      status.fireUntil = 0;
      status.cooldownUntil = now + REACTION_COOLDOWN;
      return {
        reaction: 'toxic_ignition',
        bonusDamage: baseDamage * 0.8,
        aoeRadius: 3.2,
        vulnDuration: 0,
        stunDuration: 0,
        spreadVenom: true,
        color: '#8eff5c',
      };
    }

    // 4. Singularity Shock: Void + Storm -> +55% crit burst & 0.8s micro-stun
    if ((incoming === 'storm' && hasVoid) || (incoming === 'void' && hasStorm)) {
      status.voidUntil = 0;
      status.stormUntil = 0;
      status.cooldownUntil = now + REACTION_COOLDOWN;
      return {
        reaction: 'singularity_shock',
        bonusDamage: baseDamage * 0.55,
        aoeRadius: 2.2,
        vulnDuration: 0,
        stunDuration: 0.8,
        spreadVenom: false,
        color: '#d48cff',
      };
    }
  }

  // Apply or refresh the incoming element's prime timer
  const until = now + PRIME_DURATION;
  switch (incoming) {
    case 'fire':
      status.fireUntil = Math.max(status.fireUntil, until);
      break;
    case 'frost':
      status.frostUntil = Math.max(status.frostUntil, until);
      break;
    case 'storm':
      status.stormUntil = Math.max(status.stormUntil, until);
      break;
    case 'venom':
      status.venomUntil = Math.max(status.venomUntil, until);
      break;
    case 'void':
      status.voidUntil = Math.max(status.voidUntil, until);
      break;
  }

  return NO_REACTION;
}
